import { PlusOutlined } from '@ant-design/icons';
import {
  ComunicazioneDTO,
  GetFrontRichiesteIdComunicazioniParams,
  getGetFrontComunicazioniAllegatiIdQueryOptions,
  getGetFrontRichiesteIdComunicazioniQueryKey,
  RichiestaDTO,
  useDeleteFrontComunicazioniAllegatiId,
  useDeleteFrontComunicazioniId,
  useGetFrontRichiesteIdComunicazioni,
  useGetFrontRichiesteIdTipiComunicazione,
  usePostFrontComunicazioniIdTipiAllegatoIdAllegati,
  usePostFrontComunicazioniInviaComunicazioneId,
  usePostFrontRichiesteIdComunicazioni,
  usePutFrontComunicazioniId,
  WsRispostaPaginataListComunicazioneDTO,
} from '@framework/api-client';
import { EnumRichiestaDTOStato } from '@framework/api-client/src/api/backoffice/schemas';
import { getNetworkErrorMessage, useCurrentUser } from '@framework/auth';
import {
  CommunicationButtonDrawer,
  CommunicationList,
  CommunicationsContextProvider,
  CommunicationToolbar,
  CreateCommunicationRequest,
  CreateCommunicationRequestSchema,
  SubmissionCardTabPanel,
} from '@framework/common-web';
import { useTranslation } from '@framework/i18n';
import {
  ColumnSortDirections,
  getPagination,
  handlePaginationChange,
  LoaderSpin,
  MessageFilteredList,
  useHistory,
  usePaginationUrlQueryState,
} from '@framework/ui';
import { Alert, Button, message } from 'antd';
import { isEqual, omit } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { queryClient } from '../../../core/network/queryClient';
import { SubmissionReplyCommunicationDrawer } from './SubmissionReplyCommunicationDrawer';
import { useFrontofficeCommunicationsTypes } from './useFrontofficeCommunicationTypes';

export interface ISubmissionSentTabCommunicationsProps {
  active?: boolean;
  submission?: RichiestaDTO;
}

/**
 * Tab delle comunicazioni post invio di una richiesta richiesta.
 */
export function SubmissionSentTabCommunications(
  props: ISubmissionSentTabCommunicationsProps,
) {
  const { active, submission } = props;
  const history = useHistory();
  const { t } = useTranslation();

  // Salviamo la comunicazione selezionata
  const [selectedCommunication, setSelectedCommunication] =
    useState<ComunicazioneDTO | null>(null);

  // Comunicazione ricevuta a cui si sta rispondendo (drawer dedicato).
  const [replyTarget, setReplyTarget] = useState<ComunicazioneDTO | null>(null);

  const defaultQuery = useMemo(
    () =>
      getPagination({
        page: 0,
        pageSize: 10,
        direction: ColumnSortDirections.descend,
        sort: 'dataInvio',
      }),
    [],
  );

  const [query, setQuery] =
    usePaginationUrlQueryState<GetFrontRichiesteIdComunicazioniParams>({
      history,
      input: defaultQuery,
    });

  const onPaginationChange = handlePaginationChange<
    ComunicazioneDTO,
    GetFrontRichiesteIdComunicazioniParams
  >({
    columns: [],
    defaultQuery,
    query,
    setQuery,
    getFilters: () => ({}),
  });

  const result = useGetFrontRichiesteIdComunicazioni(
    submission?.id ?? '',
    query,
    {
      query: {
        keepPreviousData: true,
        enabled: submission?.id != null,
        onSuccess: ({ dati }) => {
          if (selectedCommunication !== null && dati && dati?.length > 0) {
            dati?.forEach(comunicazione => {
              comunicazione.id === selectedCommunication.id &&
                setSelectedCommunication(comunicazione);
            });
          }
        },
      },
    },
  );

  const records = result?.data?.dati ?? [];
  const meta = result?.data?.meta;

  const isFiltered = useMemo(() => {
    if (result?.isFetching) return false;
    return !isEqual(
      omit(query, ['pagina', 'ordinamenti', 'limite']),
      omit(defaultQuery, ['pagina', 'ordinamenti', 'limite']),
    );
  }, [query, result?.isFetching, defaultQuery]);

  const { data: communicationTypeList } =
    useGetFrontRichiesteIdTipiComunicazione(
      submission?.id ?? '',
      { ordinaPerCodice: true },
      {
        query: {
          keepPreviousData: true,
          enabled: submission?.id != null,
        },
      },
    );

  // API di aggiornamento e creazione comunicazione

  const updateMutation = usePutFrontComunicazioniId({
    mutation: {
      onSuccess: async (data, variables) => {
        message.success(
          t(
            'submission-sent-tab-communications.comunicazione-modificata-con-successo',
            'Comunicazione modificata con successo',
          ),
        );
        await queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey[0] ===
            getGetFrontRichiesteIdComunicazioniQueryKey(submission!.id!)[0],
        });
        const allCommunications =
          queryClient.getQueriesData<WsRispostaPaginataListComunicazioneDTO>(
            getGetFrontRichiesteIdComunicazioniQueryKey(submission!.id!),
          )[0];
        const communication = allCommunications?.[1]?.dati?.find(
          c => c.id === variables.id,
        );
        setSelectedCommunication(communication ?? null);
        setQuery({ ...defaultQuery });
      },
      onError: error => {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-sent-tab-communications.errore-modifica-comunicazione',
              'Impossibile modificare la comunicazione',
            ),
          ),
        );
      },
    },
  });

  const createMutation = usePostFrontRichiesteIdComunicazioni({
    mutation: {
      onSuccess: async created => {
        message.success(
          t(
            'submission-sent-tab-communications.comunicazione-creata-con-successo',
            'Comunicazione creata con successo',
          ),
        );
        await queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey[0] ===
            getGetFrontRichiesteIdComunicazioniQueryKey(submission!.id!)[0],
        });
        const allCommunications =
          queryClient.getQueriesData<WsRispostaPaginataListComunicazioneDTO>(
            getGetFrontRichiesteIdComunicazioniQueryKey(submission!.id!),
          )[0];
        const communication = allCommunications?.[1].dati?.find(
          c => c.id === created.id,
        );
        setSelectedCommunication(communication ?? null);
        setQuery({ ...defaultQuery });
      },
      onError: error => {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-sent-tab-communications.errore-creazione-comunicazione',
              'Impossibile creare la comunicazione',
            ),
          ),
        );
      },
    },
  });

  // const previewMutation = usePostBackAttivitaIdComunicazioniAnteprima();

  const sendMutation = usePostFrontComunicazioniInviaComunicazioneId();
  const sendCommunication = useCallback(
    async (communicationId: string) => {
      const result = await sendMutation.mutateAsync({
        idIstanzaComunicazione: communicationId,
      });
      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdComunicazioniQueryKey(submission!.id!),
      );
      // Chiudiamo il drawer dopo l'invio
      setSelectedCommunication(null);

      return result;
    },
    [sendMutation, submission?.id, setSelectedCommunication],
  );

  const user = useCurrentUser();
  /**
   * Gestisce il salvataggio o l'invio comunicazione lato frontoffice
   */
  const handleSave = async (
    communicationRequest: CreateCommunicationRequest,
  ) => {
    try {
      if (selectedCommunication?.id != null) {
        await updateMutation.mutateAsync({
          id: selectedCommunication.id,
          data: communicationRequest,
        });
        return;
      }
      await createMutation.mutateAsync({
        id: submission?.id ?? '',
        data: {
          ...communicationRequest,
        },
      });
    } catch (e) {
      console.error(e);
      // Il messaggio è gestito negli `onError` delle mutation
    }
  };

  // Allegati
  const uploadMutation = usePostFrontComunicazioniIdTipiAllegatoIdAllegati({});
  const uploadAttachment = useCallback(
    async (communicationId: string, attachmentGroupId: string, file: Blob) => {
      try {
        const result = await uploadMutation.mutateAsync({
          id: communicationId,
          idTipoAllegato: attachmentGroupId,
          data: { file },
        });
        queryClient.invalidateQueries(
          getGetFrontRichiesteIdComunicazioniQueryKey(submission!.id!),
        );
        return result;
      } catch (error) {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-sent-tab-communications.errore-caricamento-allegato',
              "Impossibile caricare l'allegato",
            ),
          ),
        );
      }
    },
    [submission, uploadMutation],
  );

  const removeAttachmentMutation = useDeleteFrontComunicazioniAllegatiId({});
  const removeAttachment = useCallback(
    async (attachmentId: string) => {
      try {
        await removeAttachmentMutation.mutateAsync({
          idAllegato: attachmentId,
        });
        queryClient.invalidateQueries(
          getGetFrontRichiesteIdComunicazioniQueryKey(submission!.id!),
        );
      } catch (error) {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-sent-tab-communications.errore-rimozione-allegato',
              "Impossibile rimuovere l'allegato",
            ),
          ),
        );
      }
    },
    [removeAttachmentMutation, submission],
  );

  const deleteCommunicationMutation = useDeleteFrontComunicazioniId({
    mutation: {
      onSuccess: async () => {
        message.success(
          t(
            'submission-sent-tab-communications.comunicazione-eliminata-con-successo',
            'Comunicazione eliminata con successo',
          ),
        );
        await queryClient.invalidateQueries(
          getGetFrontRichiesteIdComunicazioniQueryKey(submission!.id!),
        );
      },
      onError: error => {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-sent-tab-communications.errore-eliminazione-comunicazione',
              'Impossibile eliminare la comunicaizone',
            ),
          ),
        );
      },
    },
  });
  const handleDeleteDraft = useCallback(
    async (communicationId: string) => {
      await deleteCommunicationMutation.mutateAsync({ id: communicationId });
    },
    [deleteCommunicationMutation],
  );

  // Tipi comunicazione ricercabili
  const { searchableTypes, sendableTypes, receivableTypes } =
    useFrontofficeCommunicationsTypes(submission?.id);

  if (!active) return null;

  const labelPanel = t(
    'submission-sent-tab-communications.panel-aria',
    'Tab delle comunicazioni della richiesta',
  );

  if (result?.isError) {
    return (
      <SubmissionCardTabPanel active={active} label={labelPanel}>
        <Alert
          showIcon
          type="error"
          message={t(
            'submission-sent-tab-communications.errore-caricamento-comunicazioni',
            'Impossibile caricare le comunicazioni',
          )}
          description={getNetworkErrorMessage(
            result.error,
            t(
              'submission-sent-tab-communications.errore-tab-comunicazioni',
              'Si è verificato un errore',
            ),
          )}
        />
      </SubmissionCardTabPanel>
    );
  }

  return (
    <SubmissionCardTabPanel active={active} label={labelPanel}>
      <CommunicationsContextProvider
        query={query}
        setQuery={setQuery}
        searchableTypes={searchableTypes}
        sendableTypes={sendableTypes}
        receivableTypes={receivableTypes}
        parentProcedureId={null}
        isProcedureOpen={null}
      >
        <CommunicationToolbar fieldsSize="large" />
        <MessageFilteredList
          visible={isFiltered && !result.isFetching}
          items={meta?.totale ?? null}
          message={t(
            'submission-sent-tab-communications.comunicazioni-trovate-in-base-ai-filtri',
            'comunicazioni trovate in base ai filtri di ricerca.',
          )}
          action={
            <Button
              size="small"
              children={t(
                'submission-sent-tab-communications.rimuovi-filtri',
                'Rimuovi filtri',
              )}
              onClick={() => setQuery({})}
            />
          }
        />

        <LoaderSpin spinning={result.isFetching} hasFullWidthChildren noPadding>
          <CommunicationList
            communications={records ?? []}
            onOpen={communication => setSelectedCommunication(communication)}
            onReply={communication => setReplyTarget(communication)}
            deleteDraft={handleDeleteDraft}
            isEditable={submission?.stato !== EnumRichiestaDTOStato.CONCLUSA}
            getDownloadAttachmentQueryOptions={
              getGetFrontComunicazioniAllegatiIdQueryOptions
            }
            extra={
              submission?.stato !== EnumRichiestaDTOStato.CONCLUSA &&
              (sendableTypes?.length ?? 0) > 0 && (
                <Button
                  icon={<PlusOutlined aria-hidden="true" />}
                  size={'large'}
                  type={'default'}
                  onClick={() =>
                    setSelectedCommunication(
                      CreateCommunicationRequestSchema.cast(
                        {},
                        { assert: false },
                      ),
                    )
                  }
                >
                  {t(
                    'submission-sent-tab-communications.nuova-comunicazione',
                    'Nuova Comunicazione',
                  )}
                </Button>
              )
            }
            pagination={{
              current: Number(query.pagina ?? 0) + 1,
              pageSize: query.limite != null ? Number(query.limite) : 10,
              total: meta?.totale ?? 0,
              pageSizeOptions: [5, 10, 20, 50],
              onChange: (page, pageSize) =>
                onPaginationChange({ current: page, pageSize }, {}, []),
            }}
          />
        </LoaderSpin>
        <CommunicationButtonDrawer
          communication={selectedCommunication}
          onClose={() => setSelectedCommunication(null)}
          validationSchema={CreateCommunicationRequestSchema}
          onSubmit={handleSave}
          sendCommunication={sendCommunication}
          uploadAttachment={uploadAttachment}
          removeAttachment={removeAttachment}
          getDownloadAttachmentQueryOptions={
            getGetFrontComunicazioniAllegatiIdQueryOptions
          }
        />
        <SubmissionReplyCommunicationDrawer
          submissionId={submission?.id}
          communication={replyTarget}
          onClose={() => setReplyTarget(null)}
        />
      </CommunicationsContextProvider>
    </SubmissionCardTabPanel>
  );
}
